import type {
  AffinityRecipe,
  AreaGeography,
  OperationalModelParams,
  StatisticSpec,
} from "./domain";
import { isAffinityWeight } from "./domain";

export function areaGeographyLabel(geography: AreaGeography): string {
  return {
    base_area: "Base area",
    lsoa: "LSOA",
    msoa: "MSOA",
    council_ward: "Council ward",
    county_division: "County division",
    constituency: "Constituency",
    settlement: "Settlement",
  }[geography];
}

export function statisticDisplayName(statistic: StatisticSpec): string {
  switch (statistic.provenance.kind) {
    case "ons_census":
      return `ONS Census 2021 · ${statistic.table}: ${statistic.label}`;
    case "nrs_census":
      return `NRS Census 2022 · ${statistic.table}: ${statistic.label}`;
    case "nisra_census":
      return `NISRA Census 2021 · ${statistic.table}: ${statistic.label}`;
    case "uk_parliament":
      return `UK Parliament · ${statistic.table}: estimated base-area ${statistic.label}`;
    case "eu_referendum":
      return `2016 EU referendum · estimated base-area ${statistic.label} from counting-area result`;
    case "council_election":
      return statistic.provenance.geography === "council_ward"
        ? `Principal councils · ${statistic.table}: estimated base-area ${statistic.label}`
        : `County councils · ${statistic.table}: estimated base-area ${statistic.label}`;
  }
}

export function statisticSourceLabel(statistic: StatisticSpec): string {
  switch (statistic.provenance.kind) {
    case "ons_census":
      return "ONS Census 2021";
    case "nrs_census":
      return "NRS Census 2022";
    case "nisra_census":
      return "NISRA Census 2021";
    case "uk_parliament":
      return `UK Parliament · ${statistic.table}`;
    case "council_election":
      return `${statistic.provenance.geography === "council_ward" ? "Principal councils" : "County councils"} · ${statistic.table}`;
    case "eu_referendum":
      return "2016 EU referendum results";
  }
}

export function statisticSourceGeography(statistic: StatisticSpec): string {
  switch (statistic.provenance.kind) {
    case "ons_census":
    case "nrs_census":
      return "Output Area";
    case "nisra_census":
      return "Data Zone";
    case "uk_parliament":
      return "constituency";
    case "eu_referendum":
      return "referendum counting area";
    case "council_election":
      return statistic.provenance.geography === "council_ward"
        ? "council ward"
        : "county division";
  }
}

export function projectionDescription(statistic: StatisticSpec): string | null {
  switch (statistic.provenance.kind) {
    case "ons_census":
    case "nisra_census":
      return null;
    case "nrs_census":
      return "NRS applies statistical disclosure control to published counts. Shares use the published table total and are capped at 100% where perturbation makes a category exceed that total; zero published totals remain undefined.";
    case "uk_parliament":
      return "Base-area estimate projected from constituency election results. The statistic identifies the election; source context gives its date. Best-fit assignments retain the constituency rate; percentiles rank base-area estimates.";
    case "eu_referendum":
      return "Base-area estimate projected from the observed 2016 referendum result in each historical counting area. Current centroid assignments retain the source counting-area share; percentiles rank the resulting base-area estimates.";
    case "council_election":
      return statistic.provenance.geography === "council_ward"
        ? "Base-area estimate projected from the latest applicable ordinary council ward results. The layer includes district, unitary, metropolitan, London borough, and Welsh principal councils. Percentiles rank base-area estimates."
        : "Base-area estimate projected from the latest applicable county-council division results. County divisions remain separate from council wards. Percentiles rank base-area estimates.";
  }
}

export function formatStatisticValue(
  value: number | null | undefined,
  percentage: boolean,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "No data";
  return percentage ? `${(value * 100).toFixed(1)}%` : formatNumber(value);
}

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "Unavailable";
  return Math.round(value).toLocaleString("en-GB");
}

export function formatMedianAge(value: number | null, topCodedAge: number): string {
  if (value === null) return "Unavailable";
  return value >= topCodedAge ? `${topCodedAge}+ years` : `≈ ${Math.round(value)} years`;
}

export function formatPercentile(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "Unavailable";
  return `${value.toFixed(0)}/100`;
}

export function formatDensity(value: number | null | undefined): string {
  const count = formatCount(value);
  return count === "Unavailable" ? count : `${count}/km²`;
}

export function formatCompletionHours(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "Unavailable";
  return `${value.toFixed(1)} hours`;
}

export function aggregateStatistic(
  indices: readonly number[],
  numerators: readonly (number | null)[],
  denominators: readonly (number | null)[] | null,
): { readonly value: number | null; readonly available: number; readonly total: number } {
  let numerator = 0;
  let denominator = 0;
  let available = 0;
  for (const index of indices) {
    const numeratorValue = numerators[index];
    const denominatorValue = denominators?.[index];
    if (
      numeratorValue === null ||
      numeratorValue === undefined ||
      !Number.isFinite(numeratorValue)
    ) {
      continue;
    }
    if (denominators !== null) {
      if (
        denominatorValue === null ||
        denominatorValue === undefined ||
        !Number.isFinite(denominatorValue)
      ) {
        continue;
      }
      denominator += denominatorValue;
    }
    numerator += numeratorValue;
    available += 1;
  }
  return {
    value:
      available === 0 || (denominators !== null && denominator <= 0)
        ? null
        : denominators === null
          ? numerator
          : numerator / denominator,
    available,
    total: indices.length,
  };
}

export function householdWeightedMean(
  indices: readonly number[],
  values: readonly (number | null)[],
  householdCounts: readonly (number | null)[],
): number | null {
  let weighted = 0;
  let weight = 0;
  for (const index of indices) {
    const value = values[index];
    const households = householdCounts[index];
    if (
      value === null ||
      value === undefined ||
      households === null ||
      households === undefined ||
      !Number.isFinite(value) ||
      !Number.isFinite(households) ||
      households < 0
    ) {
      continue;
    }
    weighted += value * households;
    weight += households;
  }
  return weight > 0 ? weighted / weight : null;
}

export function validateAffinityRecipe(
  recipe: AffinityRecipe,
  statistics: readonly StatisticSpec[],
): string | null {
  const specifications = new Map(statistics.map((statistic) => [statistic.id, statistic]));
  if (recipe.groups.some((group) => !isAffinityWeight(group.weightPercent))) {
    return "Affinity group weights must be integers between -100 and 100.";
  }
  const enabledGroups = recipe.groups.filter((group) => group.weightPercent !== 0);
  if (enabledGroups.length === 0) return "Give at least one affinity group a non-zero weight.";
  const used = new Set<string>();
  for (const group of enabledGroups) {
    if (group.name.trim().length === 0) return "Enabled affinity groups must have a name.";
    const subgroups = group.subgroups.filter((subgroup) => subgroup.enabled);
    if (subgroups.length === 0)
      return `Group “${group.name}” must contain an enabled subgroup.`;
    for (const subgroup of subgroups) {
      if (subgroup.statisticIds.length === 0) {
        return `A subgroup in “${group.name}” must contain at least one statistic.`;
      }
      let signature: string | null = null;
      for (const id of subgroup.statisticIds) {
        const statistic = specifications.get(id);
        if (statistic === undefined) return `Affinity statistic ${id} is unavailable.`;
        if (statistic.denominatorColumn === null) {
          return `${statisticDisplayName(statistic)} is a raw measure; affinity recipes accept percentage statistics only.`;
        }
        if (used.has(id)) return `Affinity statistic ${id} is selected more than once.`;
        used.add(id);
        const current = `${statistic.file}\u0000${statistic.denominatorColumn}`;
        if (signature !== null && current !== signature) {
          return "Statistics in a subgroup must use the same source table and denominator.";
        }
        signature = current;
      }
    }
  }
  return null;
}

export function validateOperationalParameters(
  parameters: OperationalModelParams,
): string | null {
  const finiteFields: ReadonlyArray<readonly [number, string]> = [
    [parameters.serviceSecondsPerHousehold, "Doorstep handling time"],
    [parameters.walkingSecondsAtReferenceDensity, "Walking time"],
    [parameters.referenceDensityHouseholdsPerKm2, "Reference density"],
    [parameters.fixedSetupSecondsPerBaseArea, "Fixed setup time"],
    [parameters.affinityContrastP90P10, "Affinity contrast"],
    [parameters.minimumEffectiveDensityHouseholdsPerKm2, "Minimum effective density"],
    [parameters.maximumEffectiveDensityHouseholdsPerKm2, "Maximum effective density"],
  ];
  for (const [value, label] of finiteFields) {
    if (!Number.isFinite(value)) return `${label} must be a finite number.`;
  }
  if (parameters.serviceSecondsPerHousehold <= 0) {
    return "Doorstep handling time must be greater than zero.";
  }
  if (parameters.walkingSecondsAtReferenceDensity < 0) {
    return "Walking time must be zero or greater.";
  }
  if (parameters.referenceDensityHouseholdsPerKm2 <= 0) {
    return "Reference density must be greater than zero.";
  }
  if (parameters.fixedSetupSecondsPerBaseArea < 0) {
    return "Fixed setup time must be zero or greater.";
  }
  if (parameters.affinityContrastP90P10 < 1) {
    return "Affinity contrast must be at least 1.";
  }
  if (parameters.minimumEffectiveDensityHouseholdsPerKm2 <= 0) {
    return "Minimum effective density must be greater than zero.";
  }
  if (parameters.maximumEffectiveDensityHouseholdsPerKm2 <= 0) {
    return "Maximum effective density must be greater than zero.";
  }
  if (
    parameters.minimumEffectiveDensityHouseholdsPerKm2 >
    parameters.maximumEffectiveDensityHouseholdsPerKm2
  ) {
    return "Minimum effective density must not exceed maximum effective density.";
  }
  const maximumWalkingSeconds =
    parameters.walkingSecondsAtReferenceDensity *
    (Math.sqrt(parameters.referenceDensityHouseholdsPerKm2) /
      Math.sqrt(parameters.minimumEffectiveDensityHouseholdsPerKm2));
  if (!Number.isFinite(maximumWalkingSeconds + parameters.serviceSecondsPerHousehold)) {
    return "The configured per-household operational time is too large.";
  }
  return null;
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? value.toLocaleString("en-GB") : value.toFixed(2);
}
